/**
 * ParticipantEnrichment — on-demand bios + recent news on EXTERNAL meeting
 * participants, shown in the Prep tab.
 *
 * "Recent news" is grounded in a live web_search-backed LLM call, so every
 * headline carries a real, dated source link (most recent first), followed by a
 * short professional bio and a note on recent activity. When the participant's
 * email alone is not enough to identify them, the card asks for a name + company
 * before researching. Low-confidence matches show a "may be the wrong person"
 * disclaimer. Results auto-expire after 90 days; a stale result can be regenerated.
 *
 * Self-contained: it fetches its own external-participant list (which inlines any
 * existing enrichment) from /meetings/:id/participant-enrichments, so it needs only
 * the meetingId. Renders nothing when the feature is off or there are no externals.
 */

import { useEffect, useMemo, useState } from 'react';
import { meetingsAPI } from '../../services/api';
import { decodeHtmlEntities } from '../../utils/displayText';
import {
  Newspaper, Sparkles, Loader2, AlertCircle, AlertTriangle, RotateCcw, ExternalLink, Search as UserSearch,
} from 'lucide-react';

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export default function ParticipantEnrichment({ meetingId }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Per-participant working state, keyed by participantId.
  const [enrichments, setEnrichments] = useState({});
  const [generating, setGenerating] = useState({});
  const [genError, setGenError] = useState({});
  const [identityForm, setIdentityForm] = useState({}); // { [pid]: { open, fullName, company } }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    meetingsAPI
      .listParticipantEnrichments(meetingId)
      .then((res) => {
        if (cancelled) return;
        setEnabled(res?.data?.enabled !== false);
        const list = res?.data?.participants ?? [];
        setParticipants(list);
        const seeded = {};
        for (const p of list) if (p.enrichment) seeded[p.participantId] = p.enrichment;
        setEnrichments(seeded);
        if (list.length > 0) setSelectedId((prev) => prev ?? list[0].participantId);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err?.message || 'Failed to load participants');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  const selected = useMemo(
    () => participants.find((p) => p.participantId === selectedId) ?? null,
    [participants, selectedId],
  );
  const selectedEnrichment = selectedId ? enrichments[selectedId] ?? null : null;
  const selForm = (selectedId && identityForm[selectedId]) || { open: false, fullName: '', company: '' };

  async function runGenerate(participantId, opts) {
    setGenerating((s) => ({ ...s, [participantId]: true }));
    setGenError((s) => ({ ...s, [participantId]: null }));
    try {
      const res = await meetingsAPI.generateParticipantEnrichment(meetingId, participantId, opts);
      const data = res?.data ?? {};
      if (data.needsIdentity) {
        // Open the identity form so the user can supply name + company.
        setIdentityForm((s) => ({
          ...s,
          [participantId]: { open: true, fullName: opts?.fullName || '', company: opts?.company || '' },
        }));
        if (data.enrichment) setEnrichments((s) => ({ ...s, [participantId]: data.enrichment }));
        return;
      }
      if (!data.enrichment) throw new Error('No enrichment returned');
      setEnrichments((s) => ({ ...s, [participantId]: data.enrichment }));
      setIdentityForm((s) => ({ ...s, [participantId]: { open: false, fullName: '', company: '' } }));
    } catch (err) {
      setGenError((s) => ({ ...s, [participantId]: err?.message || 'Research failed' }));
    } finally {
      setGenerating((s) => ({ ...s, [participantId]: false }));
    }
  }

  function setForm(pid, patch) {
    setIdentityForm((s) => ({ ...s, [pid]: { ...(s[pid] || { open: true, fullName: '', company: '' }), ...patch } }));
  }

  // Render nothing when disabled or there are no external participants to enrich.
  if (loading) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center gap-3 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading external participants…
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="glass rounded-2xl p-6 flex items-center gap-3 text-red-600">
        <AlertCircle className="w-5 h-5" />
        {loadError}
      </div>
    );
  }
  if (!enabled || participants.length === 0) return null;

  const needsIdentity = selectedEnrichment?.status === 'needs_identity' || selForm.open;
  const isGenerating = Boolean(selected && generating[selected.participantId]);
  const hasContent = selectedEnrichment && selectedEnrichment.status === 'ready';

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Newspaper className="w-5 h-5 text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">External participants</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Recent news and a short bio on the people joining from outside, pulled from the web so you walk in informed.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Participant picker */}
        <div className="space-y-1">
          {participants.map((p) => {
            const enr = enrichments[p.participantId];
            const active = p.participantId === selectedId;
            return (
              <button
                key={p.participantId}
                onClick={() => setSelectedId(p.participantId)}
                className={`w-full text-left px-3 py-2 rounded-xl flex items-center gap-3 transition-colors ${
                  active ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-gray-100'
                }`}
              >
                <span className="w-8 h-8 shrink-0 rounded-full bg-gradient-primary text-white text-xs font-semibold flex items-center justify-center">
                  {initials(p.displayName)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 truncate">
                    {decodeHtmlEntities(p.displayName || 'Unknown')}
                  </span>
                  {p.email && <span className="block text-xs text-gray-400 truncate">{p.email}</span>}
                </span>
                {enr?.status === 'ready' && <Newspaper className="w-4 h-4 text-green-500 shrink-0" />}
                {enr?.status === 'needs_identity' && <UserSearch className="w-4 h-4 text-amber-500 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Enrichment surface */}
        <div>
          {selected && (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <button
                  onClick={() =>
                    needsIdentity
                      ? setForm(selected.participantId, { open: true })
                      : runGenerate(selected.participantId)
                  }
                  disabled={isGenerating}
                  className="btn-primary flex items-center gap-2 disabled:opacity-60"
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : hasContent ? (
                    <RotateCcw className="w-4 h-4" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {isGenerating ? 'Researching…' : hasContent ? 'Refresh' : 'Research'}
                </button>
                {selectedEnrichment?.resolvedCompany && (
                  <span className="text-xs text-gray-400">{selectedEnrichment.resolvedCompany}</span>
                )}
                {hasContent && selectedEnrichment.stale && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">
                    <AlertTriangle className="w-3 h-3" />
                    Over 90 days old, refresh for current news
                  </span>
                )}
              </div>

              {genError[selected.participantId] && (
                <div className="mb-4 flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  {genError[selected.participantId]}
                </div>
              )}

              {/* Identity form: shown when the email alone could not identify the person. */}
              {needsIdentity && (
                <div className="mb-4 border border-amber-200 bg-amber-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-800 mb-1">
                    <UserSearch className="w-4 h-4" />
                    Who is this?
                  </div>
                  <p className="text-xs text-amber-700 mb-3">
                    The email alone isn’t enough to research the right person. Add a name (and company helps) to continue.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    <input
                      value={selForm.fullName}
                      onChange={(e) => setForm(selected.participantId, { fullName: e.target.value })}
                      placeholder="Full name"
                      className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900"
                    />
                    <input
                      value={selForm.company}
                      onChange={(e) => setForm(selected.participantId, { company: e.target.value })}
                      placeholder="Company (optional)"
                      className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900"
                    />
                  </div>
                  <button
                    onClick={() =>
                      runGenerate(selected.participantId, {
                        fullName: selForm.fullName.trim(),
                        company: selForm.company.trim() || undefined,
                      })
                    }
                    disabled={isGenerating || !selForm.fullName.trim()}
                    className="btn-primary flex items-center gap-2 disabled:opacity-60"
                  >
                    {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Research
                  </button>
                </div>
              )}

              {!selectedEnrichment && !isGenerating && !needsIdentity && (
                <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-8 text-center">
                  No research yet for {decodeHtmlEntities(selected.displayName || 'this person')}. Click Research to pull recent news and a bio.
                </div>
              )}

              {selectedEnrichment?.status === 'failed' && (
                <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl p-6 text-center">
                  Couldn’t find usable information for this person. Try Refresh, or add a name and company.
                </div>
              )}

              {hasContent && (
                <div className="space-y-5">
                  {/* Low-confidence disclaimer. */}
                  {selectedEnrichment.confidence === 'low' && (
                    <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        This may not be the right person.{' '}
                        {selectedEnrichment.disambiguationNote || 'Results were ambiguous.'} Add a name/company and refresh to narrow it down.
                      </span>
                    </div>
                  )}

                  {/* Headlines first, most recent + dated, with clickable sources. */}
                  {selectedEnrichment.headlines?.length > 0 ? (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Recent news</div>
                      <ul className="space-y-2">
                        {selectedEnrichment.headlines.map((h, i) => (
                          <li key={i} className="text-sm">
                            <a
                              href={h.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-900 font-medium hover:text-primary-600 inline-flex items-start gap-1"
                            >
                              {h.title}
                              <ExternalLink className="w-3 h-3 shrink-0 mt-1 text-gray-400" />
                            </a>
                            <div className="text-xs text-gray-400">
                              {[h.source || hostnameOf(h.url), h.publishedAt].filter(Boolean).join(' · ')}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No recent news found.</div>
                  )}

                  {/* Bio. */}
                  {selectedEnrichment.bio && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Bio</div>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedEnrichment.bio}</p>
                    </div>
                  )}

                  {/* Recent professional activity. */}
                  {selectedEnrichment.recentActivity && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Recent activity</div>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedEnrichment.recentActivity}</p>
                    </div>
                  )}

                  <div className="text-[11px] text-gray-400 pt-1">
                    Pulled from the web · researched {new Date(selectedEnrichment.updatedAt).toLocaleDateString()}. Verify before relying on it.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
